import type {
  ScriptNode,
  StatementNode,
  PipelineNode,
  CommandNode,
  SimpleCommandNode,
  WordNode,
} from '@customerio/bash-parser';

// ConditionalExpressionNode isn't exported from @customerio/bash-parser,
// so we extract it from the ConditionalCommand node type.
type ConditionalCommandNode = Extract<CommandNode, { type: 'ConditionalCommand' }>;
type ConditionalExpressionNode = ConditionalCommandNode['expression'];

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type CurlResource = {
  /** HTTP method (GET, POST, PUT, PATCH, DELETE, etc.) */
  method: string;
  /** The full URL (may contain __DYNAMIC__ placeholders for unresolved variables) */
  url: string;
  /** The resource path extracted from the URL (e.g. /v1/campaigns/123) */
  path: string;
  /** The resource type extracted from the path (e.g. "campaigns") */
  resourceType: string;
  /** The CRUD action: read (GET), create (POST), update (PUT/PATCH), delete (DELETE) */
  action: 'read' | 'create' | 'update' | 'delete';
  /** True when the URL was fully resolved — no __DYNAMIC__ placeholders.
   *  Only fully resolved resources can be used for per-ID approval. */
  fullyResolved: boolean;
};

export type AnalysisResult = {
  /**
   * How completely the script was analyzed, from least to most coverage:
   *
   * - `incomplete` — some code paths couldn't be walked (opaque commands like
   *   python3/node, unhandled AST nodes, or curl with unresolvable method).
   *   `resources` may be missing entries. Not safe for any auto-approval.
   *
   * - `all_resources_found` — every code path was walked and every curl has a
   *   known HTTP method, but some URLs contain unresolved variables. Safe for
   *   read-only auto-approval (we know no curl is mutating), but not for
   *   per-resource approval (we don't have exact paths).
   *
   * - `all_resources_found_and_resolved` — every curl was found AND every URL
   *   is fully resolved with no dynamic placeholders. Safe for per-resource
   *   approval with exact path matching.
   */
  coverage: 'incomplete' | 'all_resources_found' | 'all_resources_found_and_resolved';
  /** All curl resources found in the command. */
  resources: CurlResource[];
  /**
   * Human-readable reasons the analysis is incomplete.
   * Empty when coverage is `all_resources_found_and_resolved`.
   */
  warnings: string[];
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

function methodToAction(method: string): CurlResource['action'] {
  switch (method) {
    case 'POST':
      return 'create';
    case 'PUT':
    case 'PATCH':
      return 'update';
    case 'DELETE':
      return 'delete';
    default:
      return 'read';
  }
}

/**
 * Commands that can make network calls or hide curl invocations. These
 * always require human approval because we can't statically analyze what
 * they'll do.
 *
 * The copilot sandbox runs inside just-bash (https://github.com/vercel-labs/just-bash),
 * which provides a fixed set of commands — there's no access to arbitrary system
 * binaries. The full list of available commands is defined in the just-bash registry:
 *   https://github.com/vercel-labs/just-bash/blob/main/src/commands/registry.ts
 *
 * Builtins (eval, source, etc.) are handled by the interpreter:
 *   https://github.com/vercel-labs/just-bash/tree/main/src/interpreter/builtins
 *
 * We also register a custom `node` command via almostnode in `apps/ai-sandbox/src/node-command.ts`.
 *
 * Of all available commands, only these can make network requests or hide curl calls:
 *
 * - `node` — custom command via almostnode, can use fetch() / http module
 * - `python3`, `python` — Pyodide runtime, can use urllib / requests
 * - `bash`, `sh` — can execute arbitrary commands via `-c` flag,
 *   hiding curl calls inside string arguments we can't parse
 * - `eval` — executes string arguments as shell commands
 * - `source`, `.` — executes commands from a file in the current shell
 * - `xargs` — can invoke curl indirectly (e.g. `echo url | xargs curl`)
 * - `env` — can wrap any command (e.g. `env curl -X DELETE ...`)
 *
 * Everything else (echo, cat, grep, sed, jq, ls, rm, etc.) is safe to
 * auto-run in the sandbox. Only `curl` (analyzed for resources) and these
 * commands need scrutiny.
 */
const COMMANDS_NEEDING_APPROVAL = new Set([
  // Opaque runtimes — can make HTTP requests via their own libraries
  'node',
  'python3',
  'python',
  // Shell wrappers — can hide curl calls inside arguments we can't parse
  'bash',
  'sh',
  'eval',
  'source',
  '.',
  'xargs',
  'env',
]);

/**
 * curl options that consume the next argument as a value.
 */
const CURL_OPTIONS_WITH_VALUE = new Set([
  '-X',
  '--request',
  '-H',
  '--header',
  '-d',
  '--data',
  '--data-raw',
  '--data-binary',
  '--data-urlencode',
  '--data-ascii',
  '-F',
  '--form',
  '--form-string',
  '-o',
  '--output',
  '-u',
  '--user',
  '-A',
  '--user-agent',
  '-e',
  '--referer',
  '-b',
  '--cookie',
  '-c',
  '--cookie-jar',
  // -T / --upload-file handled separately (implies PUT)
  '-w',
  '--write-out',
  '-m',
  '--max-time',
  '--connect-timeout',
  '-r',
  '--range',
  '-x',
  '--proxy',
  '-U',
  '--proxy-user',
  '--resolve',
  '--retry',
  '--retry-delay',
  '--retry-max-time',
  '-E',
  '--cert',
  '--key',
  '--cacert',
  '--capath',
  '-K',
  '--config',
  '--interface',
  '--local-port',
  '--max-redirs',
  '--limit-rate',
  '--proto',
  '--proto-redir',
  '--dns-servers',
  '--trace',
  '--trace-ascii',
  '--stderr',
  '-D',
  '--dump-header',
  '--max-filesize',
  '--url',
]);

// ---------------------------------------------------------------------------
// Word resolution
// ---------------------------------------------------------------------------

/**
 * Placeholder substituted for dynamic content (variable expansion, command
 * substitution, etc.) when partially resolving a word. Treated the same as
 * a numeric ID by `isIdentifier` so it gets filtered out of resource paths.
 */
const DYNAMIC_PLACEHOLDER = '__DYNAMIC__';

/**
 * Resolve a WordNode to a static string value.
 * Returns `null` if the word contains dynamic content that can't be resolved
 * at parse time (variable expansion, command substitution, …).
 *
 * `nestedScripts` collects any nested ScriptNodes found in CommandSubstitution
 * or ProcessSubstitution parts so the caller can analyze them too.
 */
/**
 * Known variable values collected from assignments during the AST walk.
 * Used to resolve `$VAR` references in URLs so we get exact resource paths.
 */
type VariableMap = Map<string, string>;

function resolveWord(word: WordNode, nestedScripts?: ScriptNode[], variables?: VariableMap): string | null {
  return resolveWordImpl(word, nestedScripts, false, variables);
}

/**
 * Like `resolveWord`, but instead of returning `null` for dynamic content it
 * substitutes `DYNAMIC_PLACEHOLDER`. This preserves the static structure of
 * the word so callers (e.g. URL parsing) can still extract useful information
 * like the resource path from a URL containing variable expansions.
 */
function resolveWordPartial(word: WordNode, nestedScripts?: ScriptNode[], variables?: VariableMap): string {
  // resolveWordImpl with partial=true never returns null
  return resolveWordImpl(word, nestedScripts, true, variables) as string;
}

function resolveWordImpl(
  word: WordNode,
  nestedScripts: ScriptNode[] | undefined,
  partial: boolean,
  variables?: VariableMap,
): string | null {
  let result = '';

  // Resolve a single part node — shared between top-level and DoubleQuoted inner parts.
  // Returns the resolved string, or null if unresolvable (and not partial).
  const resolvePart = (p: {
    type: string;
    value?: string;
    parameter?: string;
    operation?: unknown;
    body?: ScriptNode;
  }): string | null => {
    if (p.type === 'Literal' || p.type === 'SingleQuoted' || p.type === 'Escaped') {
      return p.value ?? '';
    }
    if (p.type === 'CommandSubstitution') {
      if (p.body != null) nestedScripts?.push(p.body);
      if (partial) return DYNAMIC_PLACEHOLDER;
      return null;
    }
    if (p.type === 'ProcessSubstitution') {
      if (p.body != null) nestedScripts?.push(p.body);
      if (partial) return DYNAMIC_PLACEHOLDER;
      return null;
    }
    if (p.type === 'ParameterExpansion') {
      // Simple variable reference ($VAR / ${VAR}) with no operation (no default, substring, etc.)
      if (p.operation == null && p.parameter != null && variables != null && variables.has(p.parameter)) {
        return variables.get(p.parameter)!;
      }
      if (partial) return DYNAMIC_PLACEHOLDER;
      return null;
    }
    // ArithmeticExpansion, BraceExpansion, Glob, Tilde, etc.
    if (partial) return DYNAMIC_PLACEHOLDER;
    return null;
  };

  for (const part of word.parts) {
    if (part.type === 'DoubleQuoted') {
      let hasUnresolvable = false;
      for (const inner of part.parts) {
        const resolved = resolvePart(inner);
        if (resolved != null) {
          result += resolved;
        } else {
          hasUnresolvable = true;
        }
      }
      if (hasUnresolvable && !partial) return null;
    } else {
      const resolved = resolvePart(part);
      if (resolved != null) {
        result += resolved;
      } else {
        return null; // non-partial already handled inside resolvePart
      }
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Curl argument parsing
// ---------------------------------------------------------------------------

function parseCurlArgs(
  cmd: SimpleCommandNode,
  nestedScripts: ScriptNode[],
  variables?: VariableMap,
): CurlResource | null {
  const args = cmd.args.map((a) => resolveWord(a, nestedScripts, variables));
  // Partial resolution preserves static structure by substituting placeholders
  // for dynamic content (e.g. $ENV_ID → __DYNAMIC__). Used as a fallback when
  // the fully-resolved URL is null.
  // Note: pass a separate array for nested scripts to avoid double-collecting
  // (resolveWord above already collected them).
  const partialArgs = cmd.args.map((a) => resolveWordPartial(a, undefined, variables));

  let method: string | null = null;
  let url: string | null = null;
  let partialUrl: string | null = null;
  let hasData = false;

  let index = 0;
  while (index < args.length) {
    let argument = args[index];
    const partialArgument = partialArgs[index];

    if (argument == null) {
      // Even though the fully-resolved arg is null, check if it's a positional
      // arg (URL) by verifying the partial version doesn't start with '-'.
      if (partialUrl == null && !partialArgument.startsWith('-')) {
        partialUrl ??= partialArgument;
      }
      index++;
      continue;
    }

    // Normalize --option=value → split into --option + value so all
    // downstream checks work uniformly (e.g. --request=DELETE, --data=payload).
    let eqValue: string | null = null;
    if (argument.startsWith('--') && argument.includes('=')) {
      const eqIndex = argument.indexOf('=');
      eqValue = argument.slice(eqIndex + 1);
      argument = argument.slice(0, eqIndex);
    }

    if (argument === '-X' || argument === '--request') {
      const value = eqValue ?? args[index + 1];
      if (value != null) {
        method = value.toUpperCase();
      } else {
        // Method is dynamic / unresolvable — can't determine safety
        return null;
      }
      index += eqValue != null ? 1 : 2;
      continue;
    }

    // Handle -XMETHOD (short flag with value concatenated, no space)
    if (argument.startsWith('-X') && argument.length > 2) {
      method = argument.slice(2).toUpperCase();
      index++;
      continue;
    }

    if (argument === '--url') {
      const value = eqValue ?? args[index + 1];
      if (value != null) {
        url = value;
      } else {
        // Fall back to partial resolution for --url value
        const partialValue = eqValue ?? partialArgs[index + 1];
        if (partialValue != null) partialUrl ??= partialValue;
      }
      index += eqValue != null ? 1 : 2;
      continue;
    }

    if (
      argument === '-d' ||
      argument === '--data' ||
      argument === '--data-raw' ||
      argument === '--data-binary' ||
      argument === '--data-urlencode' ||
      argument === '--data-ascii' ||
      argument === '-F' ||
      argument === '--form' ||
      argument === '--form-string'
    ) {
      hasData = true;
      index += eqValue != null ? 1 : 2;
      continue;
    }

    // -T / --upload-file implies PUT
    if (argument === '-T' || argument === '--upload-file') {
      method ??= 'PUT';
      index += eqValue != null ? 1 : 2;
      continue;
    }

    if (CURL_OPTIONS_WITH_VALUE.has(argument)) {
      index += eqValue != null ? 1 : 2;
      continue;
    }

    if (argument.startsWith('-')) {
      index++;
      continue;
    }

    // Positional arg = URL
    url ??= argument;

    index++;
  }

  // Use the fully-resolved URL if available, otherwise fall back to partial.
  const resolvedUrl = url ?? partialUrl;

  if (resolvedUrl == null) {
    return null;
  }

  method ??= hasData ? 'POST' : 'GET';

  let path: string;
  try {
    path = new URL(resolvedUrl).pathname;
  } catch {
    const m = resolvedUrl.match(/(?:https?:\/\/[^/]+)?(\/[^\s#?]*)/);
    path = m?.[1] ?? resolvedUrl;
  }

  return {
    method,
    url: resolvedUrl,
    path,
    resourceType: extractResourceType(path),
    action: methodToAction(method),
    fullyResolved: !resolvedUrl.includes(DYNAMIC_PLACEHOLDER),
  };
}

// ---------------------------------------------------------------------------
// Resource type extraction
// ---------------------------------------------------------------------------

function extractResourceType(path: string): string {
  const segments = path.split('/').filter(Boolean);
  // Strip version/api prefixes unconditionally
  const meaningful = segments.filter((s) => !/^(v\d+|api)$/i.test(s));
  // Strip "environments" only when used as a namespace prefix
  // (i.e. followed by an ID then a real resource: /environments/123/campaigns).
  // Keep it when it's the terminal resource itself (/environments/123).
  const stripped: string[] = [];
  for (let index = 0; index < meaningful.length; index++) {
    if (
      /^environments$/i.test(meaningful[index]) &&
      index + 1 < meaningful.length &&
      isIdentifier(meaningful[index + 1]) &&
      index + 2 < meaningful.length
    ) {
      // environments/{id}/... — skip "environments" (namespace prefix)
      continue;
    }
    stripped.push(meaningful[index]);
  }
  const resourceParts: string[] = [];
  for (const segment of stripped) {
    if (!isIdentifier(segment)) {
      resourceParts.push(segment);
    }
  }
  const joined = resourceParts.join('/');
  if (joined === '') return path;

  // Use only the last segment as the resource type for cleaner labels.
  // Deep paths like "design-studio/workspaces/nodes/versions" become just
  // "versions", which is more understandable in the approval UI.
  const lastSegment = resourceParts[resourceParts.length - 1];
  return lastSegment;
}

function isIdentifier(segment: string): boolean {
  if (/^\d+$/.test(segment)) return true;
  if (/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(segment)) return true;
  if (/^[\da-f]{8,}$/i.test(segment)) return true;
  // Dynamic placeholders from partial word resolution (e.g. $ENV_ID → __DYNAMIC__)
  // are treated as identifiers so they get stripped from the resource type,
  // just like numeric IDs. A URL like /v1/environments/__DYNAMIC__/campaigns
  // produces resource type "campaigns".
  if (segment.includes(DYNAMIC_PLACEHOLDER)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// AST walking — exhaustive verification
// ---------------------------------------------------------------------------

type ArrayVariableMap = Map<string, string[]>;

type WalkContext = {
  resources: CurlResource[];
  warnings: string[];
  /** Tracks whether all URLs are fully resolved (no dynamic placeholders). */
  allResolved: boolean;
  /** Tracks whether every code path was walked and every curl method is known. */
  allCommandsWalked: boolean;
  /** Variable values collected from assignments during the walk. */
  variables: VariableMap;
  /** Array variable values collected from assignments (e.g. ARR=("a" "b")). */
  arrayVariables: ArrayVariableMap;
};

/** Structural warning — we couldn't walk a code path (opaque command, unknown
 *  AST node, unresolvable curl method). Drops coverage to `incomplete`.
 *
 *  Resolution-only issues (unresolved URLs where the curl was still found)
 *  don't need a warning — they're detected at the end via `hasUnresolvedResources`. */
function warnStructural(context: WalkContext, message: string) {
  context.warnings.push(message);
  context.allResolved = false;
  context.allCommandsWalked = false;
}

function walkScript(script: ScriptNode, context: WalkContext) {
  for (const stmt of script.statements) {
    walkStatement(stmt, context);
  }
}

function walkStatement(stmt: StatementNode, context: WalkContext) {
  for (const pipeline of stmt.pipelines) {
    walkPipeline(pipeline, context);
  }
}

function walkPipeline(pipeline: PipelineNode, context: WalkContext) {
  for (const cmd of pipeline.commands) {
    walkCommand(cmd, context);
  }
}

/**
 * Run `fn` with a snapshot of the current variables. Any assignments made
 * inside `fn` are reverted when it returns — variables set inside if/for/while/case
 * blocks don't leak into the outer scope.
 */
function withScopedVariables(context: WalkContext, function_: () => void) {
  const snapshot = new Map(context.variables);
  const arraySnapshot = new Map(context.arrayVariables);
  function_();
  context.variables = snapshot;
  context.arrayVariables = arraySnapshot;
}

/**
 * Try to expand for-loop word list items, resolving array references like
 * `"${ARR[@]}"` and `${ARR[*]}` into their stored values.
 *
 * Returns a flat list of resolved string values if all words can be resolved
 * (including array expansions), or `null` if any word is unresolvable.
 */
function expandForLoopWords(words: WordNode[], nestedScripts: ScriptNode[], context: WalkContext): string[] | null {
  const result: string[] = [];
  let allResolved = true;

  // Always process every word so we collect all nested scripts (command
  // substitutions like $(python3 gen.py) need to be walked even if the
  // word list can't be fully resolved). An early return would skip
  // nested script collection for later words, potentially leaving
  // dangerous commands undetected.
  for (const word of words) {
    // Check if this word is a simple array expansion: "${ARR[@]}" or ${ARR[@]}
    const arrayValues = tryResolveArrayExpansion(word, context);
    if (arrayValues != null) {
      result.push(...arrayValues);
      continue;
    }

    // Check if this word is a $(seq ...) command substitution
    const seqValues = tryEvalSeqSubstitution(word, context);
    if (seqValues != null) {
      result.push(...seqValues);
      continue;
    }

    // Fall back to regular scalar resolution
    const value = resolveWord(word, nestedScripts, context.variables);
    if (value != null) {
      result.push(value);
    } else {
      allResolved = false;
    }
  }

  return allResolved ? result : null;
}

/**
 * Check if a WordNode is a simple array expansion (`"${ARR[@]}"`, `${ARR[@]}`,
 * `"${ARR[*]}"`, or `${ARR[*]}`) and resolve it to the stored array values.
 *
 * The bash parser represents these as a ParameterExpansion with:
 * - `parameter`: `"ARR[@]"` or `"ARR[*]"`
 * - `operation`: `null`
 *
 * Returns the array values if resolvable, or `null` otherwise.
 */
function tryResolveArrayExpansion(word: WordNode, context: WalkContext): string[] | null {
  // Extract the ParameterExpansion — it may be the sole part, or wrapped in DoubleQuoted
  let paramExpansion: { type: string; parameter?: string; operation?: unknown } | null = null;

  if (word.parts.length === 1) {
    const part = word.parts[0];
    if (part.type === 'ParameterExpansion') {
      paramExpansion = part;
    } else if (part.type === 'DoubleQuoted' && part.parts.length === 1 && part.parts[0].type === 'ParameterExpansion') {
      paramExpansion = part.parts[0];
    }
  }

  if (paramExpansion == null || paramExpansion.operation != null || paramExpansion.parameter == null) {
    return null;
  }

  // Match "ARR[@]" or "ARR[*]"
  //
  // Note: In bash, "${ARR[*]}" joins all elements into a single string
  // (separated by IFS) while "${ARR[@]}" preserves them as separate words.
  // For a for loop, "${ARR[*]}" would produce one iteration with the
  // concatenated value. We intentionally treat them identically here —
  // this over-analyzes [*] (extracting more resources than would actually
  // execute), which is safe for auto-approval purposes since we never
  // approve something that shouldn't be approved. In practice, copilot
  // output almost exclusively uses [@].
  const match = paramExpansion.parameter.match(/^([A-Z_a-z]\w*)\[[*@]]$/);
  if (match == null) {
    return null;
  }

  const arrayName = match[1];
  const values = context.arrayVariables.get(arrayName);
  return values ?? null;
}

/**
 * Check if a WordNode is a `$(seq ...)` command substitution and evaluate it
 * to produce the list of values `seq` would output.
 *
 * Matches GNU coreutils `seq` behavior for all three forms:
 * - `seq LAST`            → 1, 2, ..., LAST
 * - `seq FIRST LAST`      → FIRST, FIRST+1, ..., LAST (always increment=1, no auto-descend)
 * - `seq FIRST INCR LAST` → FIRST, FIRST+INCR, ... (explicit increment, may be negative)
 *
 * Arguments can reference variables resolved from prior assignments.
 * Returns `null` if the word isn't a simple `seq` substitution or args
 * can't be resolved to integers.
 */
function tryEvalSeqSubstitution(word: WordNode, context: WalkContext): string[] | null {
  // Extract the CommandSubstitution — may be bare or inside DoubleQuoted
  let commandSub: { type: string; body: ScriptNode } | null = null;

  if (word.parts.length === 1) {
    const part = word.parts[0];
    if (part.type === 'CommandSubstitution') {
      commandSub = part as { type: string; body: ScriptNode };
    } else if (
      part.type === 'DoubleQuoted' &&
      part.parts.length === 1 &&
      part.parts[0].type === 'CommandSubstitution'
    ) {
      commandSub = part.parts[0] as { type: string; body: ScriptNode };
    }
  }

  if (commandSub == null) return null;

  // Must be a single statement, single pipeline, single simple command
  const { body } = commandSub;
  if (body.statements.length !== 1) return null;
  const stmt = body.statements[0];
  if (stmt.pipelines.length !== 1) return null;
  const pipeline = stmt.pipelines[0];
  if (pipeline.commands.length !== 1) return null;
  const cmd = pipeline.commands[0];
  if (cmd.type !== 'SimpleCommand') return null;

  const simpleCmd = cmd as SimpleCommandNode;
  if (simpleCmd.name == null) return null;

  const name = resolveWord(simpleCmd.name, undefined, context.variables);
  if (name !== 'seq') return null;

  // Resolve args to numbers
  const args = simpleCmd.args.map((a) => resolveWord(a, undefined, context.variables));
  if (args.some((a) => a == null)) return null;

  const nums = args.map((a) => Number.parseInt(a!, 10));
  if (nums.some((n) => Number.isNaN(n))) return null;

  let first: number;
  let increment: number;
  let last: number;

  if (nums.length === 1) {
    first = 1;
    increment = 1;
    last = nums[0];
  } else if (nums.length === 2) {
    first = nums[0];
    increment = 1;
    last = nums[1];
  } else if (nums.length === 3) {
    first = nums[0];
    increment = nums[1];
    last = nums[2];
    if (increment === 0) return null;
  } else {
    return null;
  }

  const MAX_SEQ_LENGTH = 10_000;
  const result: string[] = [];
  if (increment > 0) {
    for (let index = first; index <= last; index += increment) {
      if (result.length >= MAX_SEQ_LENGTH) return null;
      result.push(String(index));
    }
  } else {
    for (let index = first; index >= last; index += increment) {
      if (result.length >= MAX_SEQ_LENGTH) return null;
      result.push(String(index));
    }
  }

  return result;
}

function walkCommand(cmd: CommandNode, context: WalkContext) {
  switch (cmd.type) {
    case 'SimpleCommand':
      walkSimpleCommand(cmd, context);
      break;
    case 'If':
      // Walk all branches in scoped variable contexts — assignments inside
      // a branch may not execute, so they shouldn't leak out.
      for (const clause of cmd.clauses) {
        withScopedVariables(context, () => {
          for (const s of clause.condition) walkStatement(s, context);
          for (const s of clause.body) walkStatement(s, context);
        });
      }
      if (cmd.elseBody != null) {
        withScopedVariables(context, () => {
          for (const s of cmd.elseBody!) walkStatement(s, context);
        });
      }
      break;
    case 'For':
      // For loops with a static word list (e.g. `for id in 976 1050 1034 855`)
      // are unrolled so we get one resource per iteration with the exact ID.
      // This enables per-ID approval instead of per-type.
      if (cmd.words != null) {
        // Collect nested scripts from word-list command substitutions
        // (e.g. `for id in $(curl ...) $(python3 gen.py)`) so they get walked.
        const wordNestedScripts: ScriptNode[] = [];

        // Expand array references (e.g. "${ARR[@]}") in the word list before
        // resolving. The parser represents `${ARR[@]}` as a ParameterExpansion
        // with parameter "ARR[@]" and operation null. We expand these into the
        // stored array values so the loop can be unrolled with exact IDs.
        const expandedValues = expandForLoopWords(cmd.words, wordNestedScripts, context);

        for (const nested of wordNestedScripts) {
          withScopedVariables(context, () => walkScript(nested, context));
        }

        if (expandedValues != null) {
          for (const value of expandedValues) {
            withScopedVariables(context, () => {
              context.variables.set(cmd.variable, value);
              for (const s of cmd.body) walkStatement(s, context);
            });
          }
          break;
        }
      }
      // Dynamic word list or no words — walk once without setting the variable
      withScopedVariables(context, () => {
        for (const s of cmd.body) walkStatement(s, context);
      });
      break;
    case 'CStyleFor':
      withScopedVariables(context, () => {
        for (const s of cmd.body) walkStatement(s, context);
      });
      break;
    case 'While':
    case 'Until':
      // Walk the condition — it can contain curl/opaque commands
      // (e.g. `while curl ... | grep ...; do ... done`).
      // Condition variables are scoped along with the body.
      withScopedVariables(context, () => {
        if (cmd.condition != null) {
          for (const s of cmd.condition) walkStatement(s, context);
        }
        for (const s of cmd.body) walkStatement(s, context);
      });
      break;
    case 'Case':
      for (const item of cmd.items) {
        withScopedVariables(context, () => {
          for (const s of item.body) walkStatement(s, context);
        });
      }
      break;
    case 'Subshell':
      // Subshells get their own copy of the environment.
      withScopedVariables(context, () => {
        for (const s of cmd.body) walkStatement(s, context);
      });
      break;
    case 'Group':
      // Group commands ({ ... }) run in the same shell — variables DO leak out.
      for (const s of cmd.body) walkStatement(s, context);
      break;
    case 'FunctionDef':
      // Function body is scoped — it's a definition, not an invocation.
      withScopedVariables(context, () => {
        walkCommand(cmd.body, context);
      });
      break;
    case 'ArithmeticCommand':
      // (( )) — pure arithmetic, no command substitutions possible
      break;
    case 'ConditionalCommand':
      // [[ ]] — can contain command substitutions in word operands
      // e.g. [[ $(curl -X DELETE ...) == "ok" ]]
      walkConditionalExpression(cmd.expression, context);
      break;
    default:
      warnStructural(context, `Unhandled compound command type: ${(cmd as CommandNode).type}`);
      break;
  }
}

/**
 * Walk a [[ ]] conditional expression looking for command substitutions
 * in word operands (e.g. [[ $(curl ...) == "ok" ]]).
 */
function walkConditionalExpression(expr: ConditionalExpressionNode, context: WalkContext): void {
  switch (expr.type) {
    case 'CondBinary':
      walkWordForNested(expr.left, context);
      walkWordForNested(expr.right, context);
      break;
    case 'CondUnary':
      walkWordForNested(expr.operand, context);
      break;
    case 'CondWord':
      walkWordForNested(expr.word, context);
      break;
    case 'CondNot':
      walkConditionalExpression(expr.operand, context);
      break;
    case 'CondAnd':
    case 'CondOr':
      walkConditionalExpression(expr.left, context);
      walkConditionalExpression(expr.right, context);
      break;
    case 'CondGroup':
      walkConditionalExpression(expr.expression, context);
      break;
  }
}

function walkSimpleCommand(cmd: SimpleCommandNode, context: WalkContext) {
  // Collect variable assignments (VAR=value) into the context so later
  // commands can resolve $VAR references in URLs to exact resource paths.
  for (const assignment of cmd.assignments) {
    if (assignment.value != null) {
      // Walk nested scripts BEFORE updating the variable. In bash, the RHS
      // executes before the assignment completes, so `ENV_ID=$(curl ...$ENV_ID...)`
      // sees the old ENV_ID value inside the command substitution.
      walkWordForNested(assignment.value, context);
      const value = resolveWord(assignment.value, undefined, context.variables);
      if (value != null) {
        context.variables.set(assignment.name, value);
      } else {
        // Value is dynamic (e.g. command substitution) — remove any stale
        // known value so we don't resolve to the old one.
        context.variables.delete(assignment.name);
      }
      // Scalar assignment clears any stale array entry for the same name,
      // so a subsequent ${ARR[@]} expansion doesn't resolve to outdated values.
      context.arrayVariables.delete(assignment.name);
    } else {
      // `VAR=` with no value — assigns empty string in bash
      context.variables.set(assignment.name, '');
      context.arrayVariables.delete(assignment.name);
    }
    if (assignment.array != null) {
      const resolvedElements: string[] = [];
      let allResolved = true;
      for (const word of assignment.array) {
        walkWordForNested(word, context);
        const value = resolveWord(word, undefined, context.variables);
        if (value != null) {
          resolvedElements.push(value);
        } else {
          allResolved = false;
        }
      }
      if (allResolved) {
        context.arrayVariables.set(assignment.name, resolvedElements);
      } else {
        context.arrayVariables.delete(assignment.name);
      }
    }
  }

  // Assignment-only commands (VAR=value with no command name) — safe
  if (cmd.name == null) {
    return;
  }

  const nestedScripts: ScriptNode[] = [];
  const name = resolveWord(cmd.name, nestedScripts, context.variables);

  // Walk any nested scripts discovered in the command name itself.
  // These are subshells — variables don't leak to parent.
  for (const nested of nestedScripts) {
    withScopedVariables(context, () => walkScript(nested, context));
  }

  if (name == null) {
    // Dynamic command name (e.g. `$CMD arg1 arg2`) — can't determine what
    // it is. This is safe to auto-approve because the sandbox (just-bash)
    // only provides a fixed set of commands. If $CMD resolves to `curl` at
    // runtime, the backend's blocklist will have already flagged it for
    // approval since the variable expansion makes it opaque there too.
    // We still walk args to find any nested command substitutions.
    walkArgsForNested(cmd, context);
    return;
  }

  // `timeout` is a transparent wrapper — `timeout 5 curl -X DELETE url`
  // executes `curl -X DELETE url` after 5 seconds. Extract the wrapped
  // command and analyze it instead. timeout syntax: timeout [OPTS] DURATION CMD [ARGS]
  if (name === 'timeout') {
    const wrappedCmd = extractTimeoutWrappedCommand(cmd, context);
    if (wrappedCmd != null) {
      walkCommand(wrappedCmd, context);
    } else {
      // Can't determine what timeout wraps — flag as unanalyzable
      warnStructural(context, 'Cannot analyze: timeout with unresolvable arguments');
      walkArgsForNested(cmd, context);
    }
    return;
  }

  if (name === 'curl') {
    const argumentNestedScripts: ScriptNode[] = [];
    const resource = parseCurlArgs(cmd, argumentNestedScripts, context.variables);

    // Walk any nested scripts found in curl args (e.g. -d "$(curl ...)")
    for (const nested of argumentNestedScripts) {
      withScopedVariables(context, () => walkScript(nested, context));
    }

    if (resource != null) {
      context.resources.push(resource);
    } else {
      warnStructural(context, 'curl command with unresolvable URL');
    }
    return;
  }

  // `node -e "..."` / `node -p "..."` — if the inline code has no imports
  // or fetch calls, it's pure computation (Math.random, JSON, etc.) and safe.
  if (name === 'node' && isNodeInlineSafe(cmd, context)) {
    walkArgsForNested(cmd, context);
    return;
  }

  // `python3 -c "..."` / `python -c "..."` — if the inline code has no
  // network-capable imports, it's pure computation (math, json, etc.) and safe.
  if ((name === 'python3' || name === 'python') && isPythonInlineSafe(cmd, context)) {
    walkArgsForNested(cmd, context);
    return;
  }

  // Commands that can make network calls or hide curl invocations.
  // We can't analyze what they do, so they always need human approval.
  if (COMMANDS_NEEDING_APPROVAL.has(name)) {
    warnStructural(context, `Cannot analyze: ${name}`);
    walkArgsForNested(cmd, context);
    return;
  }

  // Everything else is safe in a sandbox — just walk args for nested scripts.
  walkArgsForNested(cmd, context);
}

/**
 * Check if a `node` command is a safe inline script (`node -e "..."`,
 * `node -p "..."`, or combined `node -pe "..."`). Safe means the code
 * string contains no `require(`,
 * `import`, or `fetch(` — without these, Node.js code can't make network
 * calls or load modules, so it's pure computation (Math.random, JSON, etc.).
 *
 * Returns `false` (unsafe) if:
 * - Not a `-e`/`-p` invocation (e.g. `node script.js`)
 * - Has `-r`/`--require`/`--import` flags (preloads modules before code runs)
 * - The code string can't be resolved
 * - The code contains `require`, `import`, `fetch`, `WebSocket`, `eval`, or `Function(`
 */
function isNodeInlineSafe(cmd: SimpleCommandNode, context: WalkContext): boolean {
  const args = cmd.args;
  let code: string | null = null;

  for (let index = 0; index < args.length; index++) {
    const argument = resolveWord(args[index], undefined, context.variables);
    // Fail closed: if we can't resolve an arg, it could be -r/--require/--import
    if (argument == null) return false;
    // -r / --require / --import preload modules before -e code runs,
    // bypassing the code string check (e.g. `node -r http -e "..."`)
    // Also handle --require=value and --import=value syntax.
    if (argument === '-r' || argument === '--require' || argument === '--import') {
      return false;
    }
    if (argument.startsWith('--require=') || argument.startsWith('--import=')) {
      return false;
    }
    if (argument === '-e' || argument === '--eval' || argument === '-p' || argument === '--print') {
      const next = args[index + 1];
      if (next != null) {
        code = resolveWord(next, undefined, context.variables);
      }
      break;
    }
    // Combined short flags: -pe means -p -e (print + eval), code follows.
    // Only -pe is valid — Node rejects -ep, -re, etc. We match strictly
    // to avoid accepting flags containing -r (preload) or unknown letters.
    if (argument === '-pe') {
      const next = args[index + 1];
      if (next != null) {
        code = resolveWord(next, undefined, context.variables);
      }
      break;
    }
  }

  if (code == null) return false;

  // Check for any way to make network calls, load modules, or execute
  // arbitrary code. Intentionally strict — matches the word anywhere,
  // not just as a call, so e.g. a comment mentioning "require" also blocks.
  if (code.includes('require')) return false;
  if (code.includes('import')) return false;
  if (code.includes('fetch')) return false;
  if (code.includes('WebSocket')) return false;
  if (code.includes('eval')) return false;
  if (code.includes('Function(')) return false;

  return true;
}

/**
 * Check if a `python3` or `python` command is a safe inline script
 * (`python3 -c "..."`). Safe means the code string contains no imports
 * of networking-capable modules — without these, Python code can't make
 * network calls, so it's pure computation (math, json, string ops, etc.).
 *
 * Returns `false` (unsafe) if:
 * - Not a `-c` invocation (e.g. `python3 script.py`)
 * - Has `-m` flag (runs a module, ignoring `-c` entirely)
 * - The code string can't be resolved
 * - The code imports or uses networking-capable modules
 */
function isPythonInlineSafe(cmd: SimpleCommandNode, context: WalkContext): boolean {
  const args = cmd.args;
  let code: string | null = null;

  for (let index = 0; index < args.length; index++) {
    const argument = resolveWord(args[index], undefined, context.variables);
    // Fail closed: if we can't resolve an arg, it could be anything
    if (argument == null) return false;
    // -m runs a module (e.g. `python3 -m http.server`), which takes
    // precedence over -c. Reject to avoid approving based on harmless
    // -c code while the module does something dangerous.
    if (argument === '-m') return false;
    // -W and -X consume the next argument as their value.
    // Without skipping, `python3 -W -c script.py` would misparse:
    // Python treats `-c` as the value for `-W` and runs `script.py`,
    // but we'd see `-c` and check the filename as inline code.
    if (argument === '-W' || argument === '-X') {
      index++; // skip the value argument
      continue;
    }
    // A positional arg (non-flag) before -c means Python runs that file,
    // with -c and its value passed as sys.argv. Reject to avoid approving
    // based on harmless -c code while a script file does something dangerous.
    if (!argument.startsWith('-')) return false;
    if (argument === '-c') {
      const next = args[index + 1];
      if (next != null) {
        code = resolveWord(next, undefined, context.variables);
      }
      break;
    }
  }

  if (code == null) return false;

  // Check for any way to make network calls or execute arbitrary code.
  // Python's networking capabilities require importing specific modules.
  // We block any code that imports or references these modules.
  //
  // Network-capable modules (CPython):
  //   urllib, requests, http, socket, aiohttp, httpx, websocket,
  //   xmlrpc, ftplib, smtplib, imaplib, poplib, telnetlib, paramiko,
  //   asyncio (has open_connection for TCP)
  //
  // Pyodide-specific network access (this runs in Pyodide, not CPython):
  //   `from js import fetch` — browser Fetch API
  //   `from pyodide import http` / `from pyodide.http import pyfetch`
  //
  // Code execution:
  //   exec, eval, compile, __import__, importlib, subprocess, ctypes
  //
  // System command execution (os module):
  //   os.system, os.popen, os.exec*, os.spawn*
  //   Also matched via bare function names (system, popen, spawn)
  //   to catch `from os import system` style imports.
  //
  // Note: Patterns use substring matching. We use bare module/function
  // names where possible to catch both `os.system(...)` and
  // `from os import system` import styles. Some patterns (like
  // `compile`, `exec`) may cause false positives on benign code
  // (e.g. `re.compile`), but this is the safe direction — blocks
  // safe code rather than allowing unsafe code.
  const unsafePatterns = [
    // Network-capable modules (CPython)
    'urllib',
    'requests',
    'http.client',
    'http.server',
    'socket',
    'aiohttp',
    'httpx',
    'websocket',
    'xmlrpc',
    'ftplib',
    'smtplib',
    'imaplib',
    'poplib',
    'telnetlib',
    'paramiko',
    'asyncio',
    // Pyodide-specific network access — the sandbox uses Pyodide,
    // where standard networking modules don't work but these do.
    // Bare 'pyodide' catches both `import pyodide` and
    // `from pyodide import http` / `from pyodide.http import pyfetch`.
    'pyodide',
    'pyfetch',
    'open_url',
    // Code execution / dynamic imports
    'exec',
    'eval',
    'compile',
    '__import__',
    'importlib',
    // System command execution — include bare function names to catch
    // `from os import system` style imports, not just `os.system()`.
    'subprocess',
    'os.system',
    'os.popen',
    'os.exec',
    'os.spawn',
    'system',
    'popen',
    'spawn',
    // Foreign function interface — can call libc system() directly
    'ctypes',
  ];

  for (const pattern of unsafePatterns) {
    if (code.includes(pattern)) return false;
  }

  // Pyodide's `js` module gives direct access to browser APIs including
  // fetch. Check for import patterns with word boundaries rather than bare
  // 'js' to avoid false positives (e.g. `import json` matching 'import js').
  if (/\bfrom\s+js\b/.test(code) || /\bimport\s+js\b/.test(code)) return false;

  return true;
}

/**
 * Extract the wrapped command from a `timeout` invocation.
 * timeout syntax: `timeout [OPTIONS] DURATION COMMAND [ARGS...]`
 * Options: -k, --kill-after, -s, --signal, --foreground, --preserve-status, -v
 * Returns a synthetic SimpleCommandNode for the wrapped command, or null.
 */
function extractTimeoutWrappedCommand(cmd: SimpleCommandNode, context: WalkContext): SimpleCommandNode | null {
  const args = cmd.args;
  let index = 0;

  // Skip timeout options
  while (index < args.length) {
    const text = resolveWord(args[index], undefined, context.variables);
    if (text == null) break;
    // Options that consume a value: -k VALUE, --kill-after=VALUE, -s VALUE, --signal=VALUE
    if (text === '-k' || text === '-s' || text === '--kill-after' || text === '--signal') {
      index += 2; // skip flag + value
      continue;
    }
    // Options with = or flags without a value
    if (
      text.startsWith('--kill-after=') ||
      text.startsWith('--signal=') ||
      text === '--foreground' ||
      text === '--preserve-status' ||
      text === '-v' ||
      text === '--verbose'
    ) {
      index += 1;
      continue;
    }
    break;
  }

  // Next arg should be the duration (number or number+suffix like 5s, 1m)
  if (index >= args.length) return null;
  index++; // skip duration

  // Remaining args are the command + its arguments
  if (index >= args.length) return null;

  return {
    type: 'SimpleCommand',
    assignments: [],
    name: args[index],
    args: args.slice(index + 1),
    redirections: cmd.redirections,
  } as SimpleCommandNode;
}

/**
 * Walk all args of a SimpleCommand looking for nested scripts
 * (CommandSubstitution, ProcessSubstitution) inside word parts.
 */
function walkArgsForNested(cmd: SimpleCommandNode, context: WalkContext) {
  for (const argument of cmd.args) {
    walkWordForNested(argument, context);
  }
}

/**
 * Walk a single WordNode looking for nested scripts.
 */
function walkWordForNested(word: WordNode, context: WalkContext) {
  for (const part of word.parts) {
    if (part.type === 'CommandSubstitution') {
      // $(...) runs in a subshell — variables don't leak to parent.
      withScopedVariables(context, () => walkScript(part.body, context));
    } else if (part.type === 'ProcessSubstitution') {
      withScopedVariables(context, () => walkScript(part.body, context));
    } else if (part.type === 'DoubleQuoted') {
      for (const inner of part.parts) {
        if (inner.type === 'CommandSubstitution') {
          withScopedVariables(context, () => walkScript(inner.body, context));
        } else if (inner.type === 'ProcessSubstitution') {
          withScopedVariables(context, () => walkScript(inner.body, context));
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Redirections
// ---------------------------------------------------------------------------
// Output redirections (>, >>, etc.) write to local files in the sandbox.
// This is safe — they don't mutate remote CIO resources. We intentionally
// do NOT flag them as unsafe.

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Analyze a bash command string and extract curl resources.
 * Commands not in `COMMANDS_NEEDING_APPROVAL` are auto-approved (sandbox is safe).
 *
 * Uses the `just-bash` AST parser to walk every command in the script —
 * including nested command substitutions, process substitutions, subshells,
 * compound commands, etc.
 *
 * @returns An `AnalysisResult` indicating whether the command is fully
 *          analyzable and which curl resources it targets.
 */
export async function analyzeBashCommand(command: string): Promise<AnalysisResult> {
  let ast: ScriptNode;
  try {
    const { parse } = await import('@customerio/bash-parser');
    ast = parse(command);
  } catch {
    return {
      coverage: 'incomplete',
      resources: [],
      warnings: ['Failed to parse command'],
    };
  }

  const context: WalkContext = {
    resources: [],
    warnings: [],
    allResolved: true,
    allCommandsWalked: true,
    variables: new Map(),
    arrayVariables: new Map(),
  };

  walkScript(ast, context);

  // If any curl resource has unresolved variables in its URL, the analysis
  // isn't fully resolved — we can't do per-ID approval without exact paths.
  const hasUnresolvedResources = context.resources.some((r) => !r.fullyResolved);
  if (hasUnresolvedResources) {
    context.allResolved = false;
  }

  // Collapse internal tracking booleans into the coverage enum
  const coverage: AnalysisResult['coverage'] = !context.allCommandsWalked
    ? 'incomplete'
    : !context.allResolved
    ? 'all_resources_found'
    : 'all_resources_found_and_resolved';

  return {
    coverage,
    resources: context.resources,
    warnings: context.warnings,
  };
}

/**
 * Given a list of curl resources, return only the mutating ones (create, update, delete).
 */
export function getMutatingResources(resources: CurlResource[]): CurlResource[] {
  return resources.filter((r) => r.action !== 'read');
}

/**
 * Group resources by their resource type for display purposes.
 */
export function groupResourcesByType(resources: CurlResource[]): Map<string, CurlResource[]> {
  const groups = new Map<string, CurlResource[]>();
  for (const resource of resources) {
    const existing = groups.get(resource.resourceType) ?? [];
    existing.push(resource);
    groups.set(resource.resourceType, existing);
  }
  return groups;
}
